
import { useLayoutEffect, useState } from 'react';
import { create } from 'zustand';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bug, Lightbulb, Send } from 'lucide-react';
import { apiErrorText, ticketsApi, collectClientInfo } from '@/shared/api';
import { AttachmentPicker, appendFiles, clipboardFiles, uploadDraftAttachments } from '@/features/tickets';
import {
  Field,
  Button,
  confirmDialog,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Input,
  Textarea,
  Tooltip,
  toast,
} from '@/shared/ui';
import type { Ticket } from '@/entities/ticket';
import { MAX_TICKET_DESCRIPTION_LENGTH } from '@/shared/config';
import { cn } from '@/shared/lib/cn';
import styles from './Header.module.css';

type ReportKind = 'bug' | 'improvement';

interface ReportDraft {
  kind: ReportKind;
  title: string;
  description: string;
  files: File[];
}

const EMPTY_DRAFT: ReportDraft = { kind: 'bug', title: '', description: '', files: [] };

const useReportDraft = create<ReportDraft & { patch: (p: Partial<ReportDraft>) => void; clear: () => void }>(
  (set) => ({
    ...EMPTY_DRAFT,
    patch: (p) => set(p),
    clear: () => set(EMPTY_DRAFT),
  }),
);

export function BugReportButton() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const { kind, title, description, files, patch, clear } = useReportDraft();
  const [sending, setSending] = useState(false);

  const setKind = (next: ReportKind) => patch({ kind: next });
  const setFiles = (next: File[]) => patch({ files: next });
  const hasDraft = title.trim() !== '' || description.trim() !== '' || files.length > 0;

  const param = searchParams.get('feedback');
  const requested: ReportKind | null = param === 'improvement' || param === 'bug' ? param : null;
  const [handledParam, setHandledParam] = useState<ReportKind | null>(null);
  if (requested !== handledParam) {
    setHandledParam(requested);
    if (requested) setOpen(true);
  }
  useLayoutEffect(() => {
    if (requested) useReportDraft.getState().patch({ kind: requested });
  }, [requested]);

  const dropFeedbackParam = () => {
    if (!searchParams.has('feedback')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('feedback');
        return next;
      },
      { replace: true },
    );
  };

  const closeDialog = () => {
    setOpen(false);
    dropFeedbackParam();
  };

  const clearDraft = async () => {
    const ok = await confirmDialog({
      title: 'Очистить сообщение?',
      description: 'Тема, текст и прикреплённые файлы пропадут.',
      confirmLabel: 'Очистить',
      danger: true,
    });
    if (ok) clear();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (sending) return;
    const list = clipboardFiles(e.clipboardData, e.target);
    if (!list) return;
    e.preventDefault();
    appendFiles(useReportDraft.getState().files, list, setFiles);
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    if (!trimmedTitle || !trimmedDescription || sending) return;
    setSending(true);
    try {
      const prefix = kind === 'bug' ? '[Баг]' : '[Улучшение]';
      const res = await ticketsApi.create({
        title: `${prefix} ${trimmedTitle}`,
        description: trimmedDescription,
        report: kind,
        priority: kind === 'bug' ? 'medium' : 'low',
        ...(kind === 'bug' ? { clientInfo: collectClientInfo() } : {}),
      });
      const created = (res as { data?: Ticket }).data;
      if (created?.id && files.length > 0) {
        try {
          await uploadDraftAttachments(created.id, files);
        } catch {
          toast.error('Заявка создана, но не все файлы загрузились — прикрепите их в чате');
        }
      }
      clear();
      closeDialog();
      toast.success(kind === 'bug' ? 'Спасибо! Баг-репорт отправлен' : 'Спасибо! Предложение отправлено');
      if (created?.id) navigate(`/tickets/view/${created.id}`);
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось создать заявку'));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Tooltip
        content={
          hasDraft ? 'Вернуться к сообщению о проблеме' : 'Сообщить о проблеме или предложить улучшение'
        }
      >
        <button
          type="button"
          className={cn(styles.iconBtn, styles.reportBtn)}
          onClick={() => setOpen(true)}
          aria-label={hasDraft ? 'Сообщить о проблеме, есть черновик' : 'Сообщить о проблеме'}
          data-tour="bug-report"
        >
          <Bug size={17} aria-hidden />
          {hasDraft && <span className={styles.draftDot} aria-hidden />}
        </button>
      </Tooltip>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
        <DialogContent onPaste={handlePaste}>
          <DialogTitle>Сообщить о проблеме</DialogTitle>
          <DialogDescription>
            Создадим заявку для команды поддержки — её прогресс можно отслеживать в «Заявках».
          </DialogDescription>
          <div className={styles.reportForm}>
            <div className={styles.reportKinds}>
              <button
                type="button"
                className={styles.kindChip}
                data-active={kind === 'bug'}
                onClick={() => setKind('bug')}
              >
                <Bug size={14} aria-hidden />
                Баг / ошибка
              </button>
              <button
                type="button"
                className={styles.kindChip}
                data-active={kind === 'improvement'}
                onClick={() => setKind('improvement')}
              >
                <Lightbulb size={14} aria-hidden />
                Улучшение
              </button>
            </div>
            <Field label="Тема">
              <Input
                value={title}
                onChange={(e) => patch({ title: e.target.value })}
                placeholder={kind === 'bug' ? 'Коротко: что сломалось?' : 'Коротко: что улучшить?'}
                maxLength={200}
              />
            </Field>
            <Field label={kind === 'bug' ? 'Что произошло' : 'Идея'}>
              <Textarea
                value={description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder={
                  kind === 'bug'
                    ? 'Что делали, что ожидали и что произошло на самом деле'
                    : 'Опишите идею и какую проблему она решает'
                }
                rows={5}
                maxLength={MAX_TICKET_DESCRIPTION_LENGTH}
              />
            </Field>
            { }
            <AttachmentPicker files={files} onChange={setFiles} disabled={sending} pasteHint />
          </div>
          <DialogFooter>
            {hasDraft && (
              <Button
                variant="ghost-danger"
                className={styles.reportClear}
                onClick={() => void clearDraft()}
                disabled={sending}
              >
                Очистить
              </Button>
            )}
            { }
            <DialogClose render={<Button variant="ghost">Свернуть</Button>} />
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={sending || !title.trim() || !description.trim()}
            >
              <Send size={14} aria-hidden />
              {sending ? 'Отправка…' : 'Отправить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
